// src/components/RoleManagement.jsx
import { useEffect, useState } from "react";
import supabase from "../../lib/supabaseClient";
import "./style.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

// Comprehensive permissions based on project features
const AVAILABLE_PERMISSIONS = [
  // User Management
  "User management",

  // Role Management
  "Role management",

  // Evaluation Features
  "Evaluation",

  // Learning Modules
  "Learning module",

  // Reports
  "Report",
];
const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserOrgId, setCurrentUserOrgId] = useState(null);

  const [formMode, setFormMode] = useState("create"); // create | edit
  const [editingRole, setEditingRole] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    role_name: "",
    role_permissions: [],
  });

  /* ================= FETCH ROLES ================= */

  useEffect(() => {
    getCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getCurrentUser() {
    try {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();
      if (userErr || !user) throw userErr || new Error("Not authenticated");
      setCurrentUserId(user.id);

      // Fetch current user's organization
      const { data: profile, error: profileErr } = await supabase
        .from("profile_table")
        .select("organization_id")
        .eq("id", user.id)
        .single();

      if (!profileErr && profile) {
        setCurrentUserOrgId(profile.organization_id);
      }

      fetchRoles(user.id, profile?.organization_id);
    } catch (err) {
      console.error("Failed to get current user:", err.message);
      setError(err.message);
    }
  }

  async function fetchRoles(userId, orgId, showTableSpinner = true) {
    if (showTableSpinner) {
      setTableLoading(true);
    }
    setLoading(true);
    try {
      let query = supabase.from("role_table").select("*");

      // If user has organization, fetch roles from all users in that organization
      if (orgId) {
        // Get all users in the same organization
        const { data: orgUsers, error: usersErr } = await supabase
          .from("profile_table")
          .select("id")
          .eq("organization_id", orgId);

        if (!usersErr && orgUsers && orgUsers.length > 0) {
          const userIds = orgUsers.map((u) => u.id);
          query = query.in("created_by", userIds);
        }
      } else {
        // If no organization, fetch roles created by this user AND by super_admin users

        // Get all super_admin role IDs
        const { data: superAdminRoles, error: roleErr } = await supabase
          .from("role_table")
          .select("id")
          .eq("role_name", "super_admin");

        if (!roleErr && superAdminRoles && superAdminRoles.length > 0) {
          const superAdminRoleIds = superAdminRoles.map(r => r.id);

          // Get all users with super_admin role
          const { data: superAdminUsers, error: usersErr } = await supabase
            .from("profile_table")
            .select("id")
            .in("row_id", superAdminRoleIds);

          if (!usersErr && superAdminUsers && superAdminUsers.length > 0) {
            const superAdminUserIds = superAdminUsers.map(u => u.id);
            // Include current user and all super_admin users
            query = query.in("created_by", [userId, ...superAdminUserIds]);
          } else {
            // If no super_admin users found, just fetch current user's roles
            query = query.eq("created_by", userId);
          }
        } else {
          // If super_admin role doesn't exist, just fetch current user's roles
          query = query.eq("created_by", userId);
        }
      }

      const { data, error } = await query.order("role_name");

      if (error) throw error;
      setRoles(data || []);
    } catch (err) {
      setError(err.message || "Failed to load roles");
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }

  /* ================= FORM HELPERS ================= */

  function resetForm() {
    setFormMode("create");
    setEditingRole(null);
    setFormData({
      role_name: "",
      role_permissions: [],
    });
    setValidationErrors({});
    setError(null);
    setSuccessMsg("");
    setShowModal(false);
  }

  function openCreateModal() {
    setFormMode("create");
    setFormData({
      role_name: "",
      role_permissions: [],
    });
    setShowModal(true);
  }

  function openEditModal(role) {
    setFormMode("edit");
    setEditingRole(role.role_name);
    setFormData({
      role_name: role.role_name,
      role_permissions: role.role_permissions || [],
    });
    setShowModal(true);
  }

  function handlePermissionToggle(permission) {
    setFormData((prev) => {
      const exists = prev.role_permissions.includes(permission);
      return {
        ...prev,
        role_permissions: exists
          ? prev.role_permissions.filter((p) => p !== permission)
          : [...prev.role_permissions, permission],
      };
    });
  }

  /* ================= CREATE / UPDATE ================= */

  async function handleSubmit(e) {
    e.preventDefault();
    setFormLoading(true);
    setError(null);
    setSuccessMsg("");
    setValidationErrors({});

    try {
      if (formMode === "create" && (!formData.role_name || !formData.role_name.trim())) {
        setValidationErrors({ role_name: "Role Name is required" });
        setFormLoading(false);
        return;
      }

      if (formMode === "create") {
        const { error } = await supabase.from("role_table").insert({
          role_name: formData.role_name.trim(),
          role_permissions: formData.role_permissions,
          created_by: currentUserId,
        });

        if (error) throw error;
        setSuccessMsg("Role created successfully");
      } else {
        const { error } = await supabase
          .from("role_table")
          .update({
            role_permissions: formData.role_permissions,
          })
          .eq("role_name", editingRole);

        if (error) throw error;
        setSuccessMsg("Role updated successfully");
      }

      resetForm();
      fetchRoles(currentUserId, currentUserOrgId, false);
    } catch (err) {
      setError(err.message || "Operation failed");
    } finally {
      setFormLoading(false);
    }
  }

  /* ================= EDIT ================= */

  function handleEdit(role) {
    openEditModal(role);
  }

  /* ================= DELETE ================= */

  // async function handleDelete(roleName) {
  //   if (!window.confirm(`Delete role "${roleName}"?`)) return;
  //
  //   setLoading(true);
  //   try {
  //     const { error } = await supabase
  //       .from('role_table')
  //       .delete()
  //       .eq('role_name', roleName);
  //
  //     if (error) throw error;
  //
  //     setSuccessMsg('Role deleted');
  //     fetchRoles(currentUserId);
  //   } catch (err) {
  //     setError(err.message || 'Delete failed');
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  return (
    <div className="rm-container">
      {/* HEADER */}
      <div className="rm-header">
        <h2>Role Management</h2>
        <button onClick={openCreateModal} className="btn btn-add">
          Add Role
        </button>
      </div>

      {/* TABLE */}
      <div className="rm-table-wrapper">
        <table className="rm-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Permissions</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={3} className="text-center" style={{ padding: '2rem' }}>
                  <LoadingSpinner message="Loading roles…" fullScreen={false} />
                </td>
              </tr>
            ) : roles.filter((role) => role.role_name !== "super_admin").length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center" style={{ padding: '2rem', color: 'var(--text-secondary)' }}>
                  No roles found
                </td>
              </tr>
            ) : (
              roles
                .filter((role) => role.role_name !== "super_admin")
                .map((role) => (
                  <tr key={role.role_name}>
                    <td>{role.role_name}</td>
                    <td>{role.role_permissions.join(", ") || "—"}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          onClick={() => handleEdit(role)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        {/* <button
                        onClick={() => handleDelete(role.role_name)}
                        className="btn-delete"
                      >
                        Delete
                      </button> */}
                      </div>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container role-modal-container">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">
                  {formMode === "create" ? "Add Role" : "Edit Role"}
                </h2>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {successMsg && (
                <div className="alert alert-success">{successMsg}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="role_name" className="form-label">
                    Role Name
                  </label>
                  <input
                    id="role_name"
                    type="text"
                    placeholder="Enter role name"
                    value={formData.role_name}
                    disabled={formMode === "edit"}
                    onChange={(e) => {
                      setFormData({ ...formData, role_name: e.target.value });
                      if (validationErrors.role_name) {
                        setValidationErrors(p => ({ ...p, role_name: null }));
                      }
                    }}
                    className={`form-input ${validationErrors.role_name ? 'error' : ''}`}
                  />
                  {formMode === "create" && validationErrors.role_name && <div className="validation-error">{validationErrors.role_name}</div>}
                </div>

                <div className="form-group">
                  <p className="form-label">Permissions</p>
                  <div className="permissions-container">
                    {AVAILABLE_PERMISSIONS.map((perm) => (
                      <label key={perm} className="permission-item">
                        <input
                          type="checkbox"
                          checked={formData.role_permissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                          className="permission-checkbox"
                        />
                        <span className="permission-text">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-submit"
                  >
                    {formLoading ? (
                      formMode === "create" ? "Adding..." : "Updating..."
                    ) : (
                      formMode === "create" ? "Add Role" : "Update Role"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn btn-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
